import { useState, useRef, FormEvent } from 'react';
import emailjs from '@emailjs/browser';

// ─── EmailJS CONFIG ────────────────────────────────────────────────────────────
// 1. Cria conta em https://www.emailjs.com (plano gratuito)
// 2. Adiciona um Email Service (Gmail ou outro) e copia o Service ID
// 3. Cria um Email Template com as variáveis abaixo e copia o Template ID
// 4. Em Account > API Keys copia a Public Key
// 5. Substitui os três valores abaixo pelos teus
const EMAILJS_SERVICE_ID  = 'SEU_SERVICE_ID';   // ex: 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'SEU_TEMPLATE_ID';  // ex: 'template_xyz456'
const EMAILJS_PUBLIC_KEY  = 'SUA_PUBLIC_KEY';   // ex: 'aBcDeFgHiJkLmNoPq'
// ──────────────────────────────────────────────────────────────────────────────

const services = [
  'Due Diligence',
  'Fusões & Aquisições',
  'Estratégia Empresarial',
  'Reestruturação Organizacional',
  'Captação de Investimento',
  'Expansão de Mercado',
  'Marketing, Comunicação e Branding',
  'Outro',
];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setStatus('sending');

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        e.currentTarget,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setStatus('success');
      formRef.current?.reset();
      setMessage('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contacto" className="bg-[#0A1F25] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left Info */}
          <div className="w-full lg:w-[40%]">
            <div className="inline-flex items-center gap-2 border border-[#026A86]/40 rounded-full px-4 py-1.5 mb-6">
              <span className="text-[#026A86] text-xs font-medium tracking-widest uppercase">
                Contacto
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Vamos conversar
              <br />
              <span className="text-[#026A86]">sobre o seu</span>
              <br />
              projecto
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-10">
              Preencha o formulário e a nossa equipa entrará em contacto em até 24 horas
              para agendar a sua consulta gratuita.
            </p>

            {/* Contact Info */}
            <div className="flex flex-col gap-5">
              {[
                { icon: 'ri-map-pin-line', label: 'Endereço', values: ['Av. Pedro de Castro Van-Dúnem,', 'Ed. 24, 1º Andar, Apt 11, Bairro Miramar', 'Luanda – Angola'], type: 'text' },
                { icon: 'ri-phone-line', label: 'Telefone', values: ['+244 925 434 499'], type: 'tel' },
                { icon: 'ri-mail-line', label: 'Email', values: ['geral@movemind.ao'], type: 'mail' },
                { icon: 'ri-time-line', label: 'Horário', values: ['Segunda a Sexta: 08:00 - 18:00'], type: 'text' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#026A86]/15 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-[#026A86]`} />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-medium uppercase tracking-wide mb-0.5">{item.label}</p>
                    {item.values.map((v) => (
                      item.type === 'tel' ? (
                        <a key={v} href={`tel:${v.replace(/\s/g, '')}`} className="block text-white text-sm hover:text-[#026A86] transition-colors duration-200 cursor-pointer">{v}</a>
                      ) : item.type === 'mail' ? (
                        <a key={v} href={`mailto:${v}`} className="block text-white text-sm hover:text-[#026A86] transition-colors duration-200 cursor-pointer">{v}</a>
                      ) : (
                        <p key={v} className="text-white text-sm">{v}</p>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>


          </div>

          {/* Right Form */}
          <div className="w-full lg:w-[60%]">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="bg-[#0D2B35] rounded-3xl p-8 md:p-10 border border-[#1a3d4a]"
            >
              <input type="hidden" name="to_email" value="geral@movemind.ao" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="text-white/50 text-xs font-medium uppercase tracking-wide block mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    required
                    placeholder="O seu nome"
                    className="w-full bg-[#011820] border border-[#1a3d4a] focus:border-[#026A86] text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-200 placeholder:text-white/20"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-medium uppercase tracking-wide block mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="email@empresa.ao"
                    className="w-full bg-[#011820] border border-[#1a3d4a] focus:border-[#026A86] text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-200 placeholder:text-white/20"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-medium uppercase tracking-wide block mb-2">
                    Empresa
                  </label>
                  <input
                    type="text"
                    name="empresa"
                    placeholder="Nome da empresa"
                    className="w-full bg-[#011820] border border-[#1a3d4a] focus:border-[#026A86] text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-200 placeholder:text-white/20"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-medium uppercase tracking-wide block mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="telefone"
                    placeholder="+244 9XX XXX XXX"
                    className="w-full bg-[#011820] border border-[#1a3d4a] focus:border-[#026A86] text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-200 placeholder:text-white/20"
                  />
                </div>
              </div>

              {/* Service */}
              <div className="mb-5">
                <label className="text-white/50 text-xs font-medium uppercase tracking-wide block mb-2">
                  Serviço de Interesse
                </label>
                <select
                  name="servico"
                  className="w-full bg-[#011820] border border-[#1a3d4a] focus:border-[#026A86] text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-200 cursor-pointer"
                >
                  <option value="">Seleccione um serviço</option>
                  {services.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="text-white/50 text-xs font-medium uppercase tracking-wide block mb-2">
                  Mensagem
                  <span className={`ml-2 ${message.length > 500 ? 'text-red-400' : 'text-white/30'}`}>
                    ({message.length}/500)
                  </span>
                </label>
                <textarea
                  name="mensagem"
                  rows={4}
                  maxLength={500}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Descreva brevemente o seu projecto ou necessidade..."
                  className="w-full bg-[#011820] border border-[#1a3d4a] focus:border-[#026A86] text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-200 placeholder:text-white/20 resize-none"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'sending' || message.length > 500}
                className="w-full flex items-center justify-center gap-3 bg-[#026A86] hover:bg-[#035f78] disabled:opacity-50 text-white font-semibold py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {status === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    A enviar...
                  </>
                ) : (
                  <>
                    Enviar Pedido de Consulta
                    <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>

              {/* Status messages */}
              {status === 'success' && (
                <div className="mt-4 flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3">
                  <i className="ri-checkbox-circle-line text-green-400" />
                  <p className="text-green-400 text-sm">
                    Pedido enviado com sucesso! Entraremos em contacto em breve.
                  </p>
                </div>
              )}
              {status === 'error' && (
                <div className="mt-4 flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line text-red-400" />
                  <p className="text-red-400 text-sm">
                    Ocorreu um erro. Por favor tente novamente.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
